import { React, useState, useEffect } from "react";
import Swal from "sweetalert2";

export default function FeatureManagementModal({
    isOpen,
    onClose,
    clinic, // Single clinic for individual mode
    selectedClinics, // Array of {id, email, name} for bulk mode
    availableFeatures,
    onSaveSingle, // (clinicId, feature, enabled) => void
    onSaveBulk, // (clinicIds, features) => void
}) {
    const [featureStates, setFeatureStates] = useState({});
    const isBulkMode = !clinic && selectedClinics && selectedClinics.length > 0;

    // Initialize feature states
    useEffect(() => {
        if (isOpen) {
            if (clinic) {
                // Single mode: use clinic's enabled_features
                const initialStates = {};
                availableFeatures.forEach(feature => {
                    const featureKey = feature.key || feature.name || feature;
                    initialStates[featureKey] = (clinic.enabled_features || []).includes(featureKey);
                });
                setFeatureStates(initialStates);
            } else {
                // Bulk mode: start with all features disabled
                const initialStates = {};
                availableFeatures.forEach(feature => {
                    const featureKey = feature.key || feature.name || feature;
                    initialStates[featureKey] = false;
                });
                setFeatureStates(initialStates);
            }
        }
    }, [isOpen, clinic, availableFeatures]);

    const handleToggle = (featureName) => {
        setFeatureStates(prev => ({
            ...prev,
            [featureName]: !prev[featureName]
        }));
    };

    // Get feature label by key
    const getFeatureLabel = (key) => {
        const feature = availableFeatures.find(f => (f.key || f.name || f) === key);
        return feature?.label || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const handleSave = () => {
        if (isBulkMode) {
            const clinicCount = selectedClinics.length;
            const enabledFeatures = Object.entries(featureStates)
                .filter(([_, enabled]) => enabled)
                .map(([key]) => getFeatureLabel(key));

            Swal.fire({
                title: `Apply features to ${clinicCount} clinic${clinicCount > 1 ? 's' : ''}?`,
                html: enabledFeatures.length > 0
                    ? `<p>Features to enable:</p><p style="color: #22c55e;">${enabledFeatures.join(', ')}</p>`
                    : '<p>No features will be enabled</p>',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, apply to all'
            }).then((result) => {
                if (result.isConfirmed) {
                    const features = Object.entries(featureStates).map(([feature, enabled]) => ({
                        feature,
                        enabled
                    }));
                    onSaveBulk(selectedClinics.map(c => c.id), features);
                    onClose();
                }
            });
        } else {
            // Single clinic mode - save changed features
            const currentEnabled = clinic.enabled_features || [];
            const changedFeatures = Object.entries(featureStates)
                .filter(([key, enabled]) => {
                    const wasEnabled = currentEnabled.includes(key);
                    return enabled !== wasEnabled;
                })
                .map(([key, enabled]) => ({ key, enabled, label: getFeatureLabel(key) }));

            if (changedFeatures.length === 0) {
                onClose();
                return;
            }

            const toEnable = changedFeatures.filter(f => f.enabled).map(f => f.label);
            const toDisable = changedFeatures.filter(f => !f.enabled).map(f => f.label);

            let changeText = '';
            if (toEnable.length > 0) {
                changeText += `<p style="color: #22c55e;">Enable: ${toEnable.join(', ')}</p>`;
            }
            if (toDisable.length > 0) {
                changeText += `<p style="color: #ef4444;">Disable: ${toDisable.join(', ')}</p>`;
            }

            Swal.fire({
                title: `Update features for ${clinic.name}?`,
                html: changeText,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, update'
            }).then((result) => {
                if (result.isConfirmed) {
                    // Save each changed feature
                    changedFeatures.forEach(({ key, enabled }) => {
                        onSaveSingle(clinic.id, key, enabled);
                    });
                    onClose();
                }
            });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <article className="relative mx-auto py-6 px-8 bg-gray-800 text-white tracking-tight font-normal rounded-lg shadow-xl" style={{ width: '500px', maxHeight: '80vh', overflow: 'auto' }}>
                {/* Header */}
                <div className="border-b border-gray-600 pb-4 mb-4">
                    <h2 className="text-xl text-center font-bold">Manage Features</h2>
                    {isBulkMode ? (
                        <p className="text-center text-gray-400 mt-1">
                            {selectedClinics.length} Clinic{selectedClinics.length > 1 ? 's' : ''} Selected
                        </p>
                    ) : (
                        <p className="text-center text-gray-400 mt-1">{clinic?.name}</p>
                    )}
                </div>

                {/* Selected Clinics List (Bulk Mode Only) */}
                {isBulkMode && (
                    <div className="mb-4">
                        <h3 className="text-sm font-semibold text-gray-400 mb-2">Selected Clinics:</h3>
                        <div
                            className="max-h-32 overflow-y-auto bg-gray-700 rounded p-2 clinic-scroll"
                            style={{
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#9ca3af #374151'
                            }}
                        >
                            <style>{`
                                .clinic-scroll::-webkit-scrollbar {
                                    width: 8px;
                                }
                                .clinic-scroll::-webkit-scrollbar-track {
                                    background: #374151;
                                    border-radius: 4px;
                                }
                                .clinic-scroll::-webkit-scrollbar-thumb {
                                    background: #9ca3af;
                                    border-radius: 4px;
                                }
                                .clinic-scroll::-webkit-scrollbar-thumb:hover {
                                    background: #d1d5db;
                                }
                            `}</style>
                            {selectedClinics.map((c) => (
                                <div key={c.id} className="text-sm py-1 border-b border-gray-600 last:border-0">
                                    <span className="text-gray-300">ID: {c.id}</span>
                                    <span className="mx-2 text-gray-500">|</span>
                                    <span className="text-gray-400">{c.email}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Features List */}
                <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-400 mb-2">Features:</h3>
                    {availableFeatures.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No features available</p>
                    ) : (
                        <div className="space-y-2">
                            {availableFeatures.map((feature) => {
                                const featureKey = feature.key || feature.name || feature;
                                const featureLabel = feature.label || featureKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                return (
                                    <label
                                        key={featureKey}
                                        className="flex items-center justify-between p-3 bg-gray-700 rounded cursor-pointer hover:bg-gray-600 transition-colors"
                                    >
                                        <span className="text-white">{featureLabel}</span>
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                className="sr-only"
                                                checked={featureStates[featureKey] || false}
                                                onChange={() => handleToggle(featureKey)}
                                            />
                                            <div className={`w-10 h-6 rounded-full transition-colors ${featureStates[featureKey] ? 'bg-green-500' : 'bg-gray-500'}`}>
                                                <div className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform absolute top-1 ${featureStates[featureKey] ? 'translate-x-5' : 'translate-x-1'}`}></div>
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-600">
                    <button
                        className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        className="px-6 py-2 bg-accent-1 hover:bg-accent-700 text-white rounded transition-colors"
                        onClick={handleSave}
                    >
                        {isBulkMode ? 'Apply to All' : 'Save'}
                    </button>
                </div>
            </article>
        </div>
    );
}
